import React, { useEffect, useState, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { Plus, Users, Building2, CheckCircle, XCircle, Loader2, Trash2, AlertTriangle, Copy, Check, MoreVertical, LogOut } from 'lucide-react';
import { Header } from '../components/layout/Header';
import { showToast } from '../components/common/Toast';
import { useOrganizationStore } from '../stores/organizationStore';
import { useAuthStore } from '../stores/authStore';
import { organizationApi } from '../services/api';
import type { Organization, OrganizationMember, JoinRequest } from '@taskfly/shared/types';

interface OrgWithMembers extends Organization {
  memberCount: number;
  userRole?: string;
  pendingRequests?: JoinRequest[];
}

export const OrganizationsPage: React.FC = () => {
  const navigate = useNavigate();
  const { organizations, fetchOrganizations, createOrganization, isLoading, error, clearError } = useOrganizationStore();
  const { user } = useAuthStore();
  
  const [orgsWithDetails, setOrgsWithDetails] = useState<OrgWithMembers[]>([]);
  const [showCreateModal, setShowCreateModal] = useState(false);
  const [showJoinModal, setShowJoinModal] = useState(false);
  const [newOrgName, setNewOrgName] = useState('');
  const [joinOrgId, setJoinOrgId] = useState('');
  const [joinOrgCode, setJoinOrgCode] = useState('');
  const [creating, setCreating] = useState(false);
  const [joining, setJoining] = useState(false);
  const [myRequests, setMyRequests] = useState<JoinRequest[]>([]);
  const [deleteTarget, setDeleteTarget] = useState<OrgWithMembers | null>(null);
  const [deleting, setDeleting] = useState(false);
  const [leaveTarget, setLeaveTarget] = useState<OrgWithMembers | null>(null);
  const [leaving, setLeaving] = useState(false);
  const [copiedId, setCopiedId] = useState<string | null>(null);
  const [openMenuId, setOpenMenuId] = useState<string | null>(null);
  const [confirmText, setConfirmText] = useState('');
  const menuRef = useRef<HTMLDivElement>(null);

  // Close context menu when clicking outside
  useEffect(() => {
    function handleClickOutside(e: MouseEvent) {
      if (menuRef.current && !menuRef.current.contains(e.target as Node)) {
        setOpenMenuId(null);
      }
    }
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  const handleCopyId = (id: string) => {
    const doCopy = async () => {
      // Prefer modern Clipboard API (requires HTTPS or localhost)
      if (navigator.clipboard && window.isSecureContext) {
        await navigator.clipboard.writeText(id);
        return;
      }
      // Fallback: create a temporary textarea and execCommand
      const textarea = document.createElement('textarea');
      textarea.value = id;
      textarea.style.position = 'fixed';
      textarea.style.opacity = '0';
      document.body.appendChild(textarea);
      textarea.focus();
      textarea.select();
      const ok = document.execCommand('copy');
      document.body.removeChild(textarea);
      if (!ok) throw new Error('execCommand copy failed');
    };

    doCopy()
      .then(() => {
        setCopiedId(id);
        setTimeout(() => setCopiedId(null), 2000);
      })
      .catch(() => {
        showToast('Failed to copy organization ID. Please try again.', 'error');
      });
  };

  useEffect(() => {
    fetchOrganizations();
    loadMyRequests();
  }, [fetchOrganizations]);

  useEffect(() => {
    if (organizations.length > 0) {
      loadOrgDetails();
    }
  }, [organizations]);

  const loadMyRequests = async () => {
    try {
      const response = await organizationApi.getMyRequests();
      if (response.success && response.data) {
        setMyRequests((response.data as JoinRequest[]).filter(r => r.status === 'PENDING'));
      }
    } catch {
      // Ignore
    }
  };

  const loadOrgDetails = async () => {
    
    const detailed: OrgWithMembers[] = [];
    
    for (const org of organizations) {
      try {
        // Always fetch members first to determine the user's role in this org
        const membersRes = await organizationApi.getMembers(org.id);
        
        let userRole = undefined;
        if (membersRes.success && membersRes.data) {
          const members = membersRes.data as OrganizationMember[];
          const myMembership = members.find(m => m.userId === user?.id);
          userRole = myMembership?.role;
        }
        
        // Only fetch pending requests if the user is ADMIN of this organization
        // This prevents 403 errors from the backend for non-admin users
        let pendingRequests: JoinRequest[] | undefined = undefined;
        if (userRole === 'ADMIN') {
          try {
            const pendingRes = await organizationApi.getPendingRequests(org.id);
            if (pendingRes.success) {
              pendingRequests = pendingRes.data as JoinRequest[];
            }
          } catch {
            // Admin but failed to load pending requests - non-fatal
          }
        }
        
        detailed.push({
          ...org,
          memberCount: membersRes.success ? (membersRes.data as OrganizationMember[]).length : 0,
          userRole,
          pendingRequests,
        });
      } catch {
        detailed.push({ ...org, memberCount: 0 });
      }
    }
    
    setOrgsWithDetails(detailed);
  };

  const handleCreateOrg = async () => {
    if (!newOrgName.trim()) return;
    setCreating(true);
    const org = await createOrganization(newOrgName.trim());
    setCreating(false);
    if (org) {
      setShowCreateModal(false);
      setNewOrgName('');
      navigate('/');
    }
  };

  const handleJoinOrg = async () => {
    if (!joinOrgCode.trim() && !joinOrgId.trim()) return;
    setJoining(true);
    try {
      const targetOrgId = joinOrgId.trim() || joinOrgCode.trim();
      const response = await organizationApi.requestJoin(targetOrgId);
      if (response.success) {
        setShowJoinModal(false);
        setJoinOrgCode('');
        setJoinOrgId('');
        loadMyRequests();
      }
    } catch {
      // Handle error
    }
    setJoining(false);
  };

  const handleCancelRequest = async (requestId: string) => {
    try {
      await organizationApi.cancelRequest(requestId);
      loadMyRequests();
    } catch {
      // Handle error
    }
  };

  const handleDeleteOrg = async () => {
    if (!deleteTarget) return;
    setDeleting(true);
    try {
      const response = await organizationApi.delete(deleteTarget.id);
      if (response.success) {
        fetchOrganizations();
        setDeleteTarget(null);
      }
    } catch {
      // Handle error
    }
    setDeleting(false);
  };

  const handleLeaveOrg = async () => {
    if (!leaveTarget) return;
    setLeaving(true);
    try {
      const response = await organizationApi.leave(leaveTarget.id);
      if (response.success) {
        setLeaveTarget(null);
        fetchOrganizations();
      }
    } catch {
      // Handle error
    }
    setLeaving(false);
  };

  const handleResolveRequest = async (orgId: string, requestId: string, status: 'APPROVED' | 'REJECTED') => {
    try {
      await organizationApi.resolveRequest(orgId, requestId, status);
      loadOrgDetails();
      loadMyRequests();
    } catch {
      // Handle error
    }
  };

  return (
    <div className="flex flex-col h-full">
      <Header title="Organizations" />
      
      <main className="flex-1 overflow-y-auto p-4 sm:p-6">
        {/* Pending requests for current user */}
        {myRequests.length > 0 && (
          <div className="mb-6 bg-amber-50 dark:bg-amber-900/20 border border-amber-200 dark:border-amber-800 rounded-lg p-4">
            <h3 className="font-medium text-amber-800 dark:text-amber-200 mb-3">Your Pending Requests</h3>
            <div className="space-y-2">
              {myRequests.map(req => (
                <div key={req.id} className="flex items-center justify-between text-sm">
                  <span className="text-amber-700 dark:text-amber-300">
                    Request to join organization
                  </span>
                  <div className="flex items-center gap-2">
                    <span className={`px-2 py-0.5 rounded text-xs ${
                        req.status === 'APPROVED' ? 'bg-green-100 text-green-700 dark:bg-green-900 dark:text-green-300' :
                        req.status === 'REJECTED' ? 'bg-red-100 text-red-700 dark:bg-red-900 dark:text-red-300' :
                        'bg-amber-100 text-amber-700 dark:bg-amber-900 dark:text-amber-300'
                      }`}>
                      {req.status === 'APPROVED' ? 'Approved' : req.status === 'REJECTED' ? 'Rejected' : 'Pending'}
                    </span>
                    <button
                      onClick={() => handleCancelRequest(req.id)}
                      className="text-amber-600 hover:text-amber-800 dark:text-amber-400 dark:hover:text-amber-200 text-xs min-h-[44px] min-w-[44px] flex items-center justify-center"
                    >
                      Cancel
                    </button>
                  </div>
                </div>
              ))}
            </div>
          </div>
        )}

        {/* Action buttons */}
        <div className="flex flex-col sm:flex-row items-stretch sm:items-center gap-3 mb-6">
          <button
            onClick={() => setShowCreateModal(true)}
            className="inline-flex items-center justify-center gap-2 px-4 py-2 min-h-[44px] bg-primary-600 text-white rounded-lg hover:bg-primary-700 transition-colors"
          >
            <Plus className="w-4 h-4" />
            Create Organization
          </button>
          <button
            onClick={() => setShowJoinModal(true)}
            className="inline-flex items-center justify-center gap-2 px-4 py-2 min-h-[44px] bg-white dark:bg-gray-800 border border-gray-300 dark:border-gray-600 text-gray-700 dark:text-gray-200 rounded-lg hover:bg-gray-50 dark:hover:bg-gray-700 transition-colors"
          >
            <Building2 className="w-4 h-4" />
            Join Organization
          </button>
        </div>

        {/* Error display */}
        {error && (
          <div className="mb-4 p-3 bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-800 rounded-lg flex items-center justify-between">
            <span className="text-red-700 dark:text-red-300 text-sm">{error}</span>
            <button onClick={clearError} className="text-red-500 hover:text-red-700">
              <XCircle className="w-4 h-4" />
            </button>
          </div>
        )}

        {/* Loading state */}
        {isLoading && organizations.length === 0 ? (
          <div className="flex items-center justify-center h-64">
            <Loader2 className="w-8 h-8 animate-spin text-primary-600" />
          </div>
        ) : organizations.length === 0 ? (
          <div className="flex flex-col items-center justify-center h-64 text-center">
            <Building2 className="w-12 h-12 text-gray-400 mb-4" />
            <div className="text-gray-900 dark:text-gray-100 font-medium mb-2">
              No organizations yet
            </div>
            <p className="text-sm text-gray-500 mb-4">
              Create or join an organization to get started
            </p>
          </div>
        ) : (
          <>
            {/* Organizations list */}
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
              {orgsWithDetails.map(org => (
                <div
                  key={org.id}
                  className="relative bg-white dark:bg-gray-800 border border-gray-200 dark:border-gray-700 rounded-lg p-4 hover:shadow-md transition-shadow"
                >
                  <div className="flex items-start justify-between mb-3">
                    <div className="flex items-center gap-3">
                      <div className="w-10 h-10 rounded-lg bg-primary-100 dark:bg-primary-900 flex items-center justify-center">
                        <Building2 className="w-5 h-5 text-primary-600 dark:text-primary-400" />
                      </div>
                      <div>
                        <h3 className="font-semibold text-gray-900 dark:text-gray-100">{org.name}</h3>
                        <p className="text-xs text-gray-500 dark:text-gray-400">
                          {org.isPersonal ? 'Personal organization' : 'Team organization'}
                        </p>
                      </div>
                    </div>
                    <div className="flex items-center gap-2">
                      {org.isPersonal && (
                        <span className="text-xs px-2 py-0.5 rounded bg-gray-100 dark:bg-gray-700 text-gray-600 dark:text-gray-300">
                          Personal
                        </span>
                      )}
                    </div>
                  </div>

                  <div className="flex items-center gap-4 text-sm text-gray-500 mb-3">
                    <div className="flex items-center gap-1">
                      <Users className="w-4 h-4" />
                      <span>{org.memberCount ?? '-'} members</span>
                    </div>
                    {org.userRole && (
                      <div className={`px-2 py-0.5 rounded text-xs font-medium ${
                        org.userRole === 'ADMIN' 
                          ? 'bg-purple-100 text-purple-700 dark:bg-purple-900 dark:text-purple-300' 
                          : 'bg-gray-100 text-gray-600 dark:bg-gray-700 dark:text-gray-300'
                      }`}>
                        {org.userRole}
                      </div>
                    )}
                  </div>

                  <div className="flex items-center gap-2 mb-3 bg-gray-50 dark:bg-gray-900/50 p-2 rounded-md border border-gray-100 dark:border-gray-700/50">
                    <span className="text-xs font-medium text-gray-500 dark:text-gray-400">ID:</span>
                    <code className="text-xs font-mono text-gray-700 dark:text-gray-300 break-all flex-1">
                      {org.id}
                    </code>
                    <button
                      onClick={() => handleCopyId(org.id)}
                      className="p-1.5 min-h-[44px] min-w-[44px] flex items-center justify-center bg-white dark:bg-gray-800 border border-gray-200 dark:border-gray-600 rounded hover:bg-gray-50 dark:hover:bg-gray-700 transition-colors flex-shrink-0 shadow-sm"
                      title="Copy full ID"
                    >
                      {copiedId === org.id ? (
                        <Check className="w-3.5 h-3.5 text-green-600 dark:text-green-400" />
                      ) : (
                        <Copy className="w-3.5 h-3.5 text-gray-500 dark:text-gray-400" />
                      )}
                    </button>
                  </div>

                  {/* 3-dot options menu for admins */}
                  {org.userRole === 'ADMIN' && (
                    <div className="absolute top-3 right-3" ref={openMenuId === org.id ? menuRef : null}>
                      <button
                        onClick={(e) => { e.stopPropagation(); setOpenMenuId(openMenuId === org.id ? null : org.id); }}
                        className="p-1.5 min-h-[44px] min-w-[44px] flex items-center justify-center rounded-lg text-gray-400 hover:text-gray-600 dark:hover:text-gray-200 hover:bg-gray-100 dark:hover:bg-gray-700 transition-colors"
                        title="Options"
                      >
                        <MoreVertical className="w-4 h-4" />
                      </button>
                      {openMenuId === org.id && (
                        <div className="absolute right-0 top-full mt-1 w-44 bg-white dark:bg-gray-800 rounded-lg shadow-lg border border-gray-200 dark:border-gray-700 py-1 z-20">
                          <button
                            onClick={() => { setOpenMenuId(null); setConfirmText(''); setDeleteTarget(org); }}
                            className="w-full px-3 py-2 text-left text-sm text-red-600 dark:text-red-400 hover:bg-red-50 dark:hover:bg-red-900/20 flex items-center gap-2 transition-colors"
                          >
                            <Trash2 className="w-4 h-4" />
                            Eliminar organización
                          </button>
                        </div>
                      )}
                    </div>
                  )}

                  {/* Leave option for non-admin members */}
                  {org.userRole && org.userRole !== 'ADMIN' && (
                    <div className="flex justify-center mt-2 pt-2 border-t border-gray-100 dark:border-gray-700">
                      <button
                        onClick={() => { setOpenMenuId(null); setLeaveTarget(org); }}
                        className="text-xs text-red-600 dark:text-red-400 hover:text-red-800 dark:hover:text-red-300 flex items-center justify-center gap-1 min-h-[44px] transition-colors"
                      >
                        <LogOut className="w-3.5 h-3.5" />
                        Leave organization
                      </button>
                    </div>
                  )}

                  {/* Pending requests for admins */}
                  {org.userRole === 'ADMIN' && org.pendingRequests && org.pendingRequests.length > 0 && (
                    <div className="border-t border-gray-100 dark:border-gray-700 pt-3 mt-3">
                      <div className="text-xs font-medium text-gray-500 mb-2">
                        Pending Requests ({org.pendingRequests.length})
                      </div>
                      <div className="space-y-2">
                        {org.pendingRequests.slice(0, 3).map(req => (
                          <div key={req.id} className="flex items-center justify-between text-sm">
                            <span className="text-gray-600 dark:text-gray-400">
                              {req.user?.firstName && req.user?.lastName
                                ? `${req.user.firstName} ${req.user.lastName}`
                                : req.user?.email || 'Unknown user'}
                            </span>
                            <div className="flex items-center gap-1">
                              <button
                                onClick={() => handleResolveRequest(org.id, req.id, 'APPROVED')}
                                className="p-1 text-green-600 hover:text-green-800 rounded min-h-[44px] min-w-[44px] flex items-center justify-center"
                                title="Approve"
                              >
                                <CheckCircle className="w-4 h-4" />
                              </button>
                              <button
                                onClick={() => handleResolveRequest(org.id, req.id, 'REJECTED')}
                                className="p-1 text-red-600 hover:text-red-800 rounded min-h-[44px] min-w-[44px] flex items-center justify-center"
                                title="Reject"
                              >
                                <XCircle className="w-4 h-4" />
                              </button>
                            </div>
                          </div>
                        ))}
                      </div>
                    </div>
                  )}
                </div>
              ))}
            </div>
          </>
        )}
      </main>

      {/* Leave Confirmation Modal */}
      {leaveTarget && (
        <div className="fixed inset-0 bg-black/50 flex items-end sm:items-center justify-center z-50 p-0 sm:p-4">
          <div className="bg-white dark:bg-gray-800 rounded-t-2xl sm:rounded-lg w-full max-w-md p-4 sm:p-6">
            <div className="flex items-center gap-3 mb-4">
              <div className="w-12 h-12 rounded-full bg-red-100 dark:bg-red-900/30 flex items-center justify-center flex-shrink-0">
                <LogOut className="w-6 h-6 text-red-600 dark:text-red-400" />
              </div>
              <div>
                <h2 className="text-lg font-semibold text-gray-900 dark:text-gray-100">
                  Leave organization
                </h2>
                <p className="text-sm text-gray-500">This action cannot be undone</p>
              </div>
            </div>

            <p className="text-gray-600 dark:text-gray-400 mb-1">
              Are you sure you want to leave{' '}
              <strong className="text-gray-900 dark:text-gray-100">{leaveTarget.name}</strong>?
            </p>

            <div className="flex flex-col-reverse sm:flex-row gap-3 sm:justify-end mt-6">
              <button
                onClick={() => setLeaveTarget(null)}
                className="px-4 py-2 text-gray-600 dark:text-gray-400 hover:bg-gray-100 dark:hover:bg-gray-700 rounded-lg w-full sm:w-auto"
              >
                Cancel
              </button>
              <button
                onClick={handleLeaveOrg}
                disabled={leaving}
                className="px-4 py-2 bg-red-600 text-white rounded-lg hover:bg-red-700 disabled:opacity-50 disabled:cursor-not-allowed flex items-center gap-2 transition-colors w-full sm:w-auto"
              >
                {leaving && <Loader2 className="w-4 h-4 animate-spin" />}
                Leave organization
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Delete Confirmation Modal */}
      {deleteTarget && (
        <div className="fixed inset-0 bg-black/50 flex items-end sm:items-center justify-center z-50 p-0 sm:p-4">
          <div className="bg-white dark:bg-gray-800 rounded-t-2xl sm:rounded-lg w-full max-w-md p-4 sm:p-6">
            <div className="flex items-center gap-3 mb-4">
              <div className="w-12 h-12 rounded-full bg-red-100 dark:bg-red-900/30 flex items-center justify-center flex-shrink-0">
                <AlertTriangle className="w-6 h-6 text-red-600 dark:text-red-400" />
              </div>
              <div>
                <h2 className="text-lg font-semibold text-gray-900 dark:text-gray-100">
                  Eliminar organización
                </h2>
                <p className="text-sm text-gray-500">Esta acción no se puede deshacer</p>
              </div>
            </div>

            <p className="text-gray-600 dark:text-gray-400 mb-1">
              ¿Estás seguro de que quieres eliminar{' '}
              <strong className="text-gray-900 dark:text-gray-100">{deleteTarget.name}</strong>?
            </p>
            <p className="text-sm text-red-600 dark:text-red-400 mb-5">
              Todos los proyectos, tareas y datos de esta organización se eliminarán de forma permanente.
            </p>

            <div className="mb-6">
              <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1.5">
                Para confirmar, escribe{' '}
                <span className="font-mono font-semibold text-gray-900 dark:text-gray-100">{deleteTarget.name}</span>
              </label>
              <input
                type="text"
                value={confirmText}
                onChange={(e) => setConfirmText(e.target.value)}
                placeholder={deleteTarget.name}
                autoFocus
                className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 text-gray-900 dark:text-gray-100 focus:outline-none focus:ring-2 focus:ring-red-500"
              />
            </div>

            <div className="flex flex-col-reverse sm:flex-row gap-3 sm:justify-end">
              <button
                onClick={() => { setDeleteTarget(null); setConfirmText(''); }}
                className="px-4 py-2 text-gray-600 dark:text-gray-400 hover:bg-gray-100 dark:hover:bg-gray-700 rounded-lg w-full sm:w-auto"
              >
                Cancelar
              </button>
              <button
                onClick={handleDeleteOrg}
                disabled={deleting || confirmText !== deleteTarget.name}
                className="px-4 py-2 bg-red-600 text-white rounded-lg hover:bg-red-700 disabled:opacity-50 disabled:cursor-not-allowed flex items-center gap-2 transition-colors w-full sm:w-auto"
              >
                {deleting && <Loader2 className="w-4 h-4 animate-spin" />}
                Eliminar organización
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Create Organization Modal */}
      {showCreateModal && (
        <div className="fixed inset-0 bg-black/50 flex items-end sm:items-center justify-center z-50 p-0 sm:p-4">
          <div className="bg-white dark:bg-gray-800 rounded-t-2xl sm:rounded-lg w-full max-w-md p-4 sm:p-6">
            <h2 className="text-lg font-semibold text-gray-900 dark:text-gray-100 mb-4">
              Create New Organization
            </h2>
            <input
              type="text"
              value={newOrgName}
              onChange={(e) => setNewOrgName(e.target.value)}
              placeholder="Organization name"
              className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 text-gray-900 dark:text-gray-100 mb-4"
              autoFocus
            />
            <div className="flex flex-col-reverse sm:flex-row gap-3 sm:justify-end">
              <button
                onClick={() => { setShowCreateModal(false); setNewOrgName(''); }}
                className="px-4 py-2 text-gray-600 dark:text-gray-400 hover:bg-gray-100 dark:hover:bg-gray-700 rounded-lg w-full sm:w-auto"
              >
                Cancel
              </button>
              <button
                onClick={handleCreateOrg}
                disabled={creating || !newOrgName.trim()}
                className="px-4 py-2 bg-primary-600 text-white rounded-lg hover:bg-primary-700 disabled:opacity-50 flex items-center gap-2 w-full sm:w-auto"
              >
                {creating && <Loader2 className="w-4 h-4 animate-spin" />}
                Create
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Join Organization Modal */}
      {showJoinModal && (
        <div className="fixed inset-0 bg-black/50 flex items-end sm:items-center justify-center z-50 p-0 sm:p-4">
          <div className="bg-white dark:bg-gray-800 rounded-t-2xl sm:rounded-lg w-full max-w-md p-4 sm:p-6">
            <h2 className="text-lg font-semibold text-gray-900 dark:text-gray-100 mb-4">
              Join Organization
            </h2>
            <input
              type="text"
              value={joinOrgCode}
              onChange={(e) => setJoinOrgCode(e.target.value)}
              placeholder="Organization ID or code"
              className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 text-gray-900 dark:text-gray-100 mb-4"
              autoFocus
            />
            <p className="text-sm text-gray-500 mb-4">
              Enter the organization ID or invite code to request to join.
            </p>
            <div className="flex flex-col-reverse sm:flex-row gap-3 sm:justify-end">
              <button
                onClick={() => { setShowJoinModal(false); setJoinOrgCode(''); }}
                className="px-4 py-2 text-gray-600 dark:text-gray-400 hover:bg-gray-100 dark:hover:bg-gray-700 rounded-lg w-full sm:w-auto"
              >
                Cancel
              </button>
              <button
                onClick={handleJoinOrg}
                disabled={joining || !joinOrgCode.trim()}
                className="px-4 py-2 bg-primary-600 text-white rounded-lg hover:bg-primary-700 disabled:opacity-50 flex items-center gap-2 w-full sm:w-auto"
              >
                {joining && <Loader2 className="w-4 h-4 animate-spin" />}
                Request to Join
              </button>
            </div>
          </div>
        </div>
      )}

    </div>
  );
};

export default OrganizationsPage;